<script setup lang="ts">
import { NButton, NCheckbox, NPopconfirm } from 'naive-ui'
import type { ListItem } from '../stores/lists'

const props = defineProps<{
  item: ListItem
  isEditing: boolean
  draggingId: number | null
  dragOverId: number | null
  isFirst: boolean
  isLast: boolean
}>()

const emit = defineEmits<{
  (e: 'toggle', id: number, done: boolean): void
  (e: 'edit', item: ListItem): void
  (e: 'delete', id: number): void
  (e: 'dragstart', event: DragEvent, id: number): void
  (e: 'dragover', event: DragEvent, id: number): void
  (e: 'dragend'): void
  (e: 'drop', event: DragEvent, id: number): void
  (e: 'move-up', id: number): void
  (e: 'move-down', id: number): void
}>()

const PRIORITY_LABELS: Record<number, string> = { 1: 'High', 2: 'Medium', 3: 'Low' }
const PRIORITY_CLASSES: Record<number, string> = { 1: 'text-red-400', 2: 'text-yellow-400' }

function priorityLabel(p: number | null) {
  return p ? (PRIORITY_LABELS[p] ?? null) : null
}

function priorityClass(p: number | null) {
  return (p != null && PRIORITY_CLASSES[p]) || 'text-white/50'
}
</script>

<template>
  <div
    draggable="true"
    class="flex items-start gap-2 bg-white/5 rounded-lg p-3 cursor-grab active:cursor-grabbing"
    :class="{
      'opacity-40': item.done,
      'ring-1 ring-white/20': isEditing,
      'ring-1 ring-blue-400/60 bg-blue-400/5': dragOverId === item.id,
      'opacity-30 scale-95': draggingId === item.id,
    }"
    @dragstart="emit('dragstart', $event, item.id)"
    @dragover="emit('dragover', $event, item.id)"
    @dragend="emit('dragend')"
    @drop="emit('drop', $event, item.id)"
  >
    <span
      class="text-white/20 select-none text-lg leading-tight mt-0.5 hover:text-white/40 flex-shrink-0"
      role="button"
      tabindex="0"
      :aria-label="`Drag to reorder: ${item.title}`"
      >⠿</span
    >
    <NCheckbox
      :checked="item.done"
      :aria-label="`Mark ${item.title} as done`"
      @update:checked="(v) => emit('toggle', item.id, v)"
    />
    <div class="flex-1 min-w-0">
      <p class="text-sm text-white m-0" :class="{ 'line-through': item.done }">{{ item.title }}</p>
      <p v-if="item.url" class="text-xs text-white/50 m-0 mt-0.5 truncate">
        <a :href="item.url" target="_blank" rel="noopener" class="text-blue-400 hover:underline">{{ item.url }}</a>
      </p>
      <p v-if="item.notes" class="text-xs text-white/40 m-0 mt-0.5">{{ item.notes }}</p>
      <p v-if="item.priority" class="text-xs m-0 mt-0.5" :class="priorityClass(item.priority)">
        Priority: {{ priorityLabel(item.priority) }}
      </p>
    </div>
    <div class="flex flex-col gap-0.5 flex-shrink-0">
      <NButton
        size="tiny"
        quaternary
        :disabled="isFirst"
        :aria-label="`Move ${item.title} up`"
        @click="emit('move-up', item.id)"
        >↑</NButton
      >
      <NButton
        size="tiny"
        quaternary
        :disabled="isLast"
        :aria-label="`Move ${item.title} down`"
        @click="emit('move-down', item.id)"
        >↓</NButton
      >
    </div>
    <NButton size="tiny" quaternary :aria-label="`Edit ${item.title}`" @click="emit('edit', item)">Edit</NButton>
    <NPopconfirm positive-text="Remove" negative-text="Cancel" @positive-click="emit('delete', item.id)">
      <template #trigger>
        <NButton size="tiny" quaternary type="error" :aria-label="`Delete ${item.title}`">Delete</NButton>
      </template>
      Remove "{{ item.title }}" from {{ item.list }}?
    </NPopconfirm>
  </div>
</template>
